export class DegradeError extends Error {
	public isDegraded: boolean = true;
}

type DegradationReason = 'timeout' | 'failure';

export const DEGRADE_TIMEOUT_MS = 6000; // default timeout
const DEGRADE_ERROR = new DegradeError('Promise is degraded');

interface DegradablePromiseInterface<T> {
	/**
	 * Retrieves the `DegradablePromise` as a `Promise` so handlers like `then` and `catch` can then be attached.
	 */
	promise: () => Promise<T>;
}

export type DegradationCallback = (reason: DegradationReason) => void;

export class DegradablePromise<T> implements DegradablePromiseInterface<T> {
	private degradablePromiseInternal: Promise<T>;
	private degradeTimeout: ReturnType<typeof setTimeout>;
	private timeoutMs: number;

	private degradeInternal: () => void = () => {
		// Default throws an error as this should never happen
		throw new Error('Programming error, cancelling promise is not properly rejected');
	};

	constructor(
		wrapped: Promise<T>,
		degradationCallback: DegradationCallback,
		timeoutMs: number = DEGRADE_TIMEOUT_MS,
	) {
		const degrader = new Promise<T>((_, reject) => {
			this.degradeInternal = () => {
				reject(DEGRADE_ERROR);
				degradationCallback('timeout');
			};
		});

		// Call the degradation callback with 'failed' if the wrapped promise fails, easy way to track metrics at fetch level
		wrapped.catch(() => {
			degradationCallback('failure');
		});

		// Build a finally chain into the wrapped promise so if it resolves early, we cancel the degradation
		const wrappedFinally = wrapped.finally(() => {
			if (this.degradeTimeout) {
				clearTimeout(this.degradeTimeout);
			}
		});

		const wrapper = Promise.race<T>([wrappedFinally, degrader]);

		this.degradablePromiseInternal = wrapper;

		this.timeoutMs = timeoutMs ?? DEGRADE_TIMEOUT_MS;
		this.degradeTimeout = setTimeout(this.timeout, this.timeoutMs);
	}

	private timeout: () => void = () => {
		this.degradeInternal();
	};

	public promise: () => Promise<T> = () => this.degradablePromiseInternal;

	public static from<T>(
		wrapped: Promise<T>,
		degradationCallback: DegradationCallback = () => {},
		timeoutMs?: number,
	): DegradablePromise<T> {
		return new DegradablePromise(wrapped, degradationCallback, timeoutMs ?? DEGRADE_TIMEOUT_MS);
	}
}
